import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ChatModal } from './components';
import {
  AccessDeniedError,
  fetchAgentResponse,
  fetchStreamingResponse,
  RateLimitError,
  UnauthorizedError,
  getSessionId,
  initDocumentSession,
  resetSession,
  setSessionId,
} from './services/api';
import {
  registerChatButtonConfig,
  subscribeOpenChatModal,
} from './utils/eventBus';
import { submitFeedback } from './services/feedback';
import { environment } from 'nsdi-volto-frontend/config/environments';
import type { OpenChatEventDetail } from './utils/eventBus';
import type {
  ChatMessage,
  MessageRatingValue,
  VoyagerEnhancedSearchBlockData,
} from './types';
import { getSessionStorage } from '../../../utils/browserStorage';
import './VoyagerEnhancedSearch.scss';

interface VoyagerEnhancedSearchBlockProps {
  data?: VoyagerEnhancedSearchBlockData;
  isEditMode?: boolean;
}

const CHAT_HISTORY_STORAGE_KEY = 'voyagerEnhancedSearch.chatHistory';
const SYSTEM_LIMIT_MESSAGE =
  'The AI chat system has reached its usage limit for the day. Please try again tomorrow.';
const SESSION_LIMIT_MESSAGE =
  'This chat has reached its usage limit. Start a new chat to continue when usage is available.';
const DEFAULT_GREETING_MESSAGE =
  'Hello! Welcome to AskNSDI.\n\nPrototype Disclaimer: This AI search tool is intended for testing and requirements gathering. Responses may be inaccurate and are not official. Please verify results with the authoritative source.\n\nWhat geospatial data are you looking for?';

interface StoredChatHistory {
  sessionId: string;
  messages: Array<Omit<ChatMessage, 'timestamp'> & { timestamp: string }>;
  seenMessageIds: string[];
}

const hasActiveSessionToken = (): boolean => {
  try {
    return (
      typeof sessionStorage !== 'undefined' &&
      Boolean(sessionStorage.getItem('sessionToken'))
    );
  } catch {
    return false;
  }
};

const hasUsableSessionToken = (): boolean => {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    const token = sessionStorage.getItem('sessionToken');
    return Boolean(token && token !== 'local-dev-bypass');
  } catch {
    return false;
  }
};

const loadStoredChatHistory = (): StoredChatHistory | null => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(CHAT_HISTORY_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredChatHistory>;
    if (!parsed.sessionId || !Array.isArray(parsed.messages)) return null;

    return {
      sessionId: parsed.sessionId,
      messages: parsed.messages,
      seenMessageIds: Array.isArray(parsed.seenMessageIds)
        ? parsed.seenMessageIds.filter(
            (id): id is string => typeof id === 'string',
          )
        : [],
    };
  } catch {
    return null;
  }
};

const saveStoredChatHistory = (
  sessionId: string,
  messages: ChatMessage[],
  seenMessageIds: Set<string>,
): void => {
  try {
    if (typeof localStorage === 'undefined') return;

    const payload: StoredChatHistory = {
      sessionId,
      messages: messages.map((message) => ({
        ...message,
        isStreaming: false,
        timestamp:
          message.timestamp instanceof Date
            ? message.timestamp.toISOString()
            : new Date(message.timestamp).toISOString(),
      })),
      seenMessageIds: Array.from(seenMessageIds),
    };

    localStorage.setItem(CHAT_HISTORY_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage may be unavailable in private browsing or SSR.
  }
};

const clearStoredChatHistory = (): void => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(CHAT_HISTORY_STORAGE_KEY);
    }
  } catch {
    // Storage may be unavailable in private browsing or SSR.
  }
};

const restoreMessages = (
  messages: StoredChatHistory['messages'],
): ChatMessage[] =>
  messages
    .filter(
      (message) =>
        typeof message.id === 'string' &&
        (message.role === 'user' || message.role === 'assistant') &&
        typeof message.content === 'string',
    )
    .map((message) => ({
      ...message,
      timestamp: new Date(message.timestamp),
      isStreaming: false,
    }));

const getGeneratedQueryUrl = (message: ChatMessage): string => {
  const toolData = (message.tool_data || {}) as Record<string, unknown>;
  return (
    (typeof toolData.generated_query_url === 'string' &&
      toolData.generated_query_url) ||
    (typeof toolData.generatedQueryUrl === 'string' &&
      toolData.generatedQueryUrl) ||
    ''
  );
};

const getLatestGeneratedQueryUrl = (messages: ChatMessage[]): string => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg.role !== 'assistant' || msg.isStreaming) continue;

    const generatedQueryUrl = getGeneratedQueryUrl(msg);
    if (generatedQueryUrl) {
      return generatedQueryUrl;
    }
  }

  return '';
};

const getNow = (): number =>
  typeof performance !== 'undefined' ? performance.now() : Date.now();

/**
 * VoyagerEnhancedSearch Block
 *
 * Displays a floating action button (FAB) that opens a chat modal
 * when the user enables enhanced search. The chat interface allows
 * natural language queries that get converted to search URLs.
 *
 * Chat state is owned here (not inside the modal) so conversations
 * persist across open/close. "New Chat" resets explicitly.
 *
 * seenMessageIds tracks which messages have already been displayed so
 * they render instantly (no typewriter replay) when modal reopens.
 */
const VoyagerEnhancedSearchBlock: React.FC<VoyagerEnhancedSearchBlockProps> = ({
  data,
  isEditMode = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionLimitReached, setIsSessionLimitReached] = useState(false);
  const [systemLimitMessage, setSystemLimitMessage] = useState<string | null>(
    null,
  );
  const [needsReVerify, setNeedsReVerify] = useState(false);
  const seenMessageIds = useRef<Set<string>>(new Set());
  const lastFailedMessage = useRef<string | null>(null);
  const inFlightRequest = useRef(false);
  const documentInitInFlight = useRef(false);
  const pendingDocumentRequest = useRef<OpenChatEventDetail | null>(null);
  const lastAppliedQueryUrl = useRef<string>('');
  const lastHandledRateLimitTest = useRef<number | undefined>(undefined);
  const hasRestoredChatHistory = useRef(false);
  // Latest messages snapshot for lookups inside async callbacks (feedback).
  const messagesRef = useRef<ChatMessage[]>([]);

  // Block configuration with defaults
  const headerTitle = data?.headerTitle?.trim() || 'Enhanced Search';
  const headerSubtitle =
    data?.headerSubtitle?.trim() ||
    'Natural language to advanced search queries';
  const greetingMessage =
    data?.greetingMessage != null && data.greetingMessage.trim() !== ''
      ? data.greetingMessage
      : DEFAULT_GREETING_MESSAGE;
  const projectInfoUrl = data?.projectInfoUrl?.trim() || '';
  const projectInfoLinkText =
    data?.projectInfoLinkText?.trim() || 'Learn more about this project';
  const placeholder = data?.placeholder || 'What are you looking for?';
  const searchPagePath = data?.searchPagePath || '/voyager-search';
  const agentEndpoint = data?.agentEndpoint || '';
  const selectedEndpoint: '/chat2' | '/chat3' =
    data?.selectedChatEndpoint === '/chat3' ? '/chat3' : '/chat2';
  const verificationBaseUrl = data?.verificationEndpoint || '';
  const turnstileSiteKey = data?.turnstileSiteKey || '';
  const allowLocalDevBypass = data?.allowLocalDevBypass === true;
  const showDebugInfo = data?.showDebugInfo === true;
  const showSessionId = data?.showSessionId !== false;
  const autoApplyGeneratedSearch = data?.autoApplyGeneratedSearch === true;
  const searchUrl = data?.searchUrl?.trim() || '';
  const fabPosition = data?.fabPosition || 'bottom-right';
  const chatWithPdfButtonText =
    data?.chatWithPdfButtonText?.trim() || 'Chat with PDF';
  const feedbackEndpoint =
    data?.feedbackEndpoint?.trim() ||
    environment.enhancedSearch.feedbackApiUrl ||
    '';
  const feedbackEnabled = Boolean(feedbackEndpoint);
  const feedbackStyle: 'stars' | 'thumbs' =
    data?.feedbackStyle === 'stars' ? 'stars' : 'thumbs';
  const normalizeTags = (value: unknown): string[] =>
    Array.isArray(value)
      ? value
          .map((tag) => (typeof tag === 'string' ? tag.trim() : ''))
          .filter((tag) => tag !== '')
      : [];
  const feedbackTagsPositive = useMemo(
    () =>
      normalizeTags(
        // Fall back to the legacy single list for previously saved blocks.
        data?.feedbackTagsPositive ?? data?.feedbackTags,
      ),
    [data?.feedbackTagsPositive, data?.feedbackTags],
  );
  const feedbackTagsNegative = useMemo(
    () => normalizeTags(data?.feedbackTagsNegative),
    [data?.feedbackTagsNegative],
  );

  useEffect(() => {
    if (!isEditMode || !data?.testRateLimitNonce) {
      return;
    }

    if (lastHandledRateLimitTest.current === data.testRateLimitNonce) {
      return;
    }

    lastHandledRateLimitTest.current = data.testRateLimitNonce;
    inFlightRequest.current = false;
    setIsLoading(false);

    if (data.testRateLimitScenario === 'system') {
      setMessages([]);
      setIsSessionLimitReached(false);
      setSystemLimitMessage(SYSTEM_LIMIT_MESSAGE);
      setIsModalOpen(false);
      return;
    }

    if (data.testRateLimitScenario === 'session') {
      setSystemLimitMessage(null);
      setIsSessionLimitReached(true);
      setIsModalOpen(true);
      setMessages([
        {
          id: `test-user-${Date.now()}`,
          role: 'user',
          content: 'Test follow-up request',
          timestamp: new Date(),
        },
        {
          id: `test-limit-${Date.now()}`,
          role: 'assistant',
          content: `**Usage limit reached:** ${SESSION_LIMIT_MESSAGE}`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [data?.testRateLimitNonce, data?.testRateLimitScenario, isEditMode]);

  useEffect(() => {
    if (hasRestoredChatHistory.current) {
      return;
    }

    hasRestoredChatHistory.current = true;

    if (!hasActiveSessionToken()) {
      clearStoredChatHistory();
      return;
    }

    const stored = loadStoredChatHistory();
    if (!stored) {
      return;
    }

    const restoredMessages = restoreMessages(stored.messages);
    if (restoredMessages.length === 0) {
      clearStoredChatHistory();
      return;
    }

    setSessionId(stored.sessionId);
    lastAppliedQueryUrl.current = getLatestGeneratedQueryUrl(restoredMessages);
    setMessages(restoredMessages);
    seenMessageIds.current = new Set(stored.seenMessageIds);
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!hasRestoredChatHistory.current) {
      return;
    }

    if (!hasActiveSessionToken()) {
      clearStoredChatHistory();
      return;
    }

    if (messages.length === 0) {
      return;
    }

    saveStoredChatHistory(getSessionId(), messages, seenMessageIds.current);
  }, [messages]);

  const handleSubmitFeedback = useCallback(
    async (
      messageId: string,
      value: MessageRatingValue,
      comment: string,
      tags: string[],
    ) => {
      if (!feedbackEndpoint) {
        return;
      }

      const current = messagesRef.current;
      const message = current.find((m) => m.id === messageId);

      await submitFeedback(feedbackEndpoint, {
        sessionId: getSessionId(),
        traceId: message?.traceId,
        rating: value,
        comment,
        tags,
      });

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                rating: {
                  value,
                  comment: comment || undefined,
                  submittedAt: new Date().toISOString(),
                },
              }
            : m,
        ),
      );
    },
    [feedbackEndpoint],
  );

  const initializeDocumentChat = useCallback(
    async (detail: OpenChatEventDetail) => {
      if (documentInitInFlight.current) {
        return;
      }

      if (!detail.documentUrl) {
        pendingDocumentRequest.current = null;
        setMessages([
          {
            id: `document-error-${Date.now()}`,
            role: 'assistant',
            content:
              '**Document not opened:** this result does not include a PDF URL.',
            timestamp: new Date(),
          },
        ]);
        return;
      }

      documentInitInFlight.current = true;
      inFlightRequest.current = true;
      lastFailedMessage.current = null;
      setIsLoading(true);
      setIsSessionLimitReached(false);
      setSystemLimitMessage(null);
      setMessages([]);
      seenMessageIds.current.clear();
      clearStoredChatHistory();
      const requestStartedAt = getNow();

      try {
        const document = await initDocumentSession(
          detail.documentUrl,
          detail.documentTitle,
          selectedEndpoint,
          agentEndpoint,
        );

        pendingDocumentRequest.current = null;
        setMessages([
          {
            id: `document-loaded-${Date.now()}`,
            role: 'assistant',
            content: `Opened "${document.title}" for PDF chat. Ask a question about this document to continue.`,
            tool_data: {
              generated_query_url: '',
              chart_data: [],
              document_data: {
                title: document.title,
                pages: String(document.pages),
                url: document.url,
              },
            },
            responseTimeMs: Math.round(getNow() - requestStartedAt),
            timestamp: new Date(),
          },
        ]);
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          pendingDocumentRequest.current = detail;
          sessionStorage.removeItem('sessionToken');
          clearStoredChatHistory();
          setNeedsReVerify(true);
          return;
        }

        if (err instanceof RateLimitError) {
          pendingDocumentRequest.current = null;
          setMessages([]);
          setSystemLimitMessage(err.message || SYSTEM_LIMIT_MESSAGE);
          setIsModalOpen(false);
          return;
        }

        let errorMessage =
          'The agent could not open this PDF document. Try another PDF result.';
        if (err instanceof AccessDeniedError) {
          errorMessage =
            'Access denied. You do not have permission to open this PDF document.';
        } else if (err instanceof Error && err.message) {
          errorMessage = err.message;
        }

        pendingDocumentRequest.current = null;
        setMessages([
          {
            id: `document-error-${Date.now()}`,
            role: 'assistant',
            content: `**Document not opened:** ${errorMessage}`,
            responseTimeMs: Math.round(getNow() - requestStartedAt),
            timestamp: new Date(),
          },
        ]);
      } finally {
        documentInitInFlight.current = false;
        inFlightRequest.current = false;
        setIsLoading(false);
      }
    },
    [agentEndpoint, selectedEndpoint],
  );

  const handleChatVerified = useCallback(() => {
    const pending = pendingDocumentRequest.current;
    if (pending) {
      void initializeDocumentChat(pending);
    }
  }, [initializeDocumentChat]);

  useEffect(() => {
    if (isEditMode) {
      return undefined;
    }

    return registerChatButtonConfig({ chatWithPdfButtonText });
  }, [chatWithPdfButtonText, isEditMode]);

  useEffect(() => {
    if (isEditMode) {
      return undefined;
    }

    return subscribeOpenChatModal((detail) => {
      pendingDocumentRequest.current = detail;
      setIsModalOpen(true);

      if (hasUsableSessionToken()) {
        void initializeDocumentChat(detail);
      }
    });
  }, [initializeDocumentChat, isEditMode]);

  /* ------------------------------------------------------------------ */
  /*  JSON handler (/chat2)                                              */
  /* ------------------------------------------------------------------ */

  const handleSendJson = useCallback(
    async (userText: string) => {
      if (inFlightRequest.current || isSessionLimitReached) return;
      const isNewSessionRequest = messages.length === 0;
      inFlightRequest.current = true;
      lastFailedMessage.current = null;
      setSystemLimitMessage(null);
      const requestStartedAt = getNow();

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: userText,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const response = await fetchAgentResponse(
          userText,
          agentEndpoint,
          searchUrl,
          (attempt, max) => {
            // Show retry progress as a temporary loading hint
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              const retryMsg: ChatMessage = {
                id: 'retry-status',
                role: 'assistant',
                content: `Agent is warming up... retrying (${attempt}/${max})`,
                timestamp: new Date(),
              };
              // Replace existing retry status or append
              if (last?.id === 'retry-status') {
                return [...prev.slice(0, -1), retryMsg];
              }
              return [...prev, retryMsg];
            });
          },
        );

        // Remove retry status message if present, then add real response
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== 'retry-status');
          const assistantMsg: ChatMessage = {
            id: response.id,
            role: 'assistant',
            content: response.content,
            tool_data: response.tool_data,
            traceId: response.traceId,
            responseTimeMs: Math.round(getNow() - requestStartedAt),
            timestamp: new Date(),
          };
          return [...filtered, assistantMsg];
        });
      } catch (err) {
        // 401 — session token expired: drop back to the verification widget
        if (err instanceof UnauthorizedError) {
          getSessionStorage()?.removeItem('sessionToken');
          clearStoredChatHistory();
          setNeedsReVerify(true);
          setMessages((prev) => prev.filter((m) => m.id !== 'retry-status'));
          return;
        }

        if (err instanceof RateLimitError) {
          if (isNewSessionRequest || err.scope === 'system') {
            setMessages([]);
            setSystemLimitMessage(err.message || SYSTEM_LIMIT_MESSAGE);
            setIsModalOpen(false);
            return;
          }

          lastFailedMessage.current = userText;
          setIsSessionLimitReached(true);
          setMessages((prev) => {
            const filtered = prev.filter((m) => m.id !== 'retry-status');
            const errorMsg: ChatMessage = {
              id: `error-${Date.now()}`,
              role: 'assistant',
              content: `**Usage limit reached:** ${err.message || SESSION_LIMIT_MESSAGE}`,
              responseTimeMs: Math.round(getNow() - requestStartedAt),
              timestamp: new Date(),
            };
            return [...filtered, errorMsg];
          });
          return;
        }

        let errorMessage =
          'The agent could not process this request in time. Try a simpler question first, then drill down for details.';
        if (err instanceof AccessDeniedError) {
          errorMessage =
            'Access denied. You do not have permission to perform this action.';
        }

        lastFailedMessage.current = userText;
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== 'retry-status');
          const errorMsg: ChatMessage = {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: `**Error:** ${errorMessage}`,
            responseTimeMs: Math.round(getNow() - requestStartedAt),
            timestamp: new Date(),
          };
          return [...filtered, errorMsg];
        });
      } finally {
        inFlightRequest.current = false;
        setIsLoading(false);
      }
    },
    [agentEndpoint, isSessionLimitReached, messages.length, searchUrl],
  );

  const handleSendStreaming = useCallback(
    (userText: string) => {
      if (inFlightRequest.current || isSessionLimitReached) return;
      const isNewSessionRequest = messages.length === 0;
      inFlightRequest.current = true;
      lastFailedMessage.current = null;
      setSystemLimitMessage(null);
      const requestStartedAt = getNow();

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: userText,
        timestamp: new Date(),
      };
      const streamId = `stream-${Date.now()}`;
      const streamingMsg: ChatMessage = {
        id: streamId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMsg, streamingMsg]);
      setIsLoading(true);

      fetchStreamingResponse(
        userText,
        {
          onDelta: (textSoFar) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamId
                  ? { ...m, content: textSoFar, isStreaming: true }
                  : m,
              ),
            );
          },
          onDone: (finalText, toolData, traceId) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === streamId
                  ? {
                      ...m,
                      content: finalText,
                      tool_data: toolData,
                      traceId,
                      isStreaming: false,
                      responseTimeMs: Math.round(getNow() - requestStartedAt),
                    }
                  : m,
              ),
            );
            inFlightRequest.current = false;
            setIsLoading(false);
          },
          onError: (err) => {
            inFlightRequest.current = false;
            if (err instanceof UnauthorizedError) {
              getSessionStorage()?.removeItem('sessionToken');
              clearStoredChatHistory();
              setNeedsReVerify(true);
              setMessages((prev) => prev.filter((m) => m.id !== streamId));
              setIsLoading(false);
              return;
            }

            if (err instanceof RateLimitError) {
              if (isNewSessionRequest || err.scope === 'system') {
                setMessages([]);
                setSystemLimitMessage(err.message || SYSTEM_LIMIT_MESSAGE);
                setIsModalOpen(false);
                setIsLoading(false);
                return;
              }

              lastFailedMessage.current = userText;
              setIsSessionLimitReached(true);
              setMessages((prev) => {
                const filtered = prev.filter((m) => m.id !== streamId);
                const errorMsg: ChatMessage = {
                  id: `error-${Date.now()}`,
                  role: 'assistant',
                  content: `**Usage limit reached:** ${err.message || SESSION_LIMIT_MESSAGE}`,
                  responseTimeMs: Math.round(getNow() - requestStartedAt),
                  timestamp: new Date(),
                };
                return [...filtered, errorMsg];
              });
              setIsLoading(false);
              return;
            }

            let errorMessage =
              'The agent could not process this request in time. Try a simpler question first, then drill down for details.';
            if (err instanceof AccessDeniedError) {
              errorMessage =
                'Access denied. You do not have permission to perform this action.';
            }

            lastFailedMessage.current = userText;
            setMessages((prev) => {
              const filtered = prev.filter((m) => m.id !== streamId);
              const errorMsg: ChatMessage = {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `**Error:** ${errorMessage}`,
                responseTimeMs: Math.round(getNow() - requestStartedAt),
                timestamp: new Date(),
              };
              return [...filtered, errorMsg];
            });
            setIsLoading(false);
          },
        },
        agentEndpoint,
        searchUrl,
      );
    },
    [agentEndpoint, isSessionLimitReached, messages.length, searchUrl],
  );

  /* ------------------------------------------------------------------ */
  /*  Unified send — dispatches based on selected endpoint               */
  /* ------------------------------------------------------------------ */

  const handleSend = useCallback(
    (userText: string) => {
      if (selectedEndpoint === '/chat3') {
        handleSendStreaming(userText);
      } else {
        handleSendJson(userText);
      }
    },
    [selectedEndpoint, handleSendStreaming, handleSendJson],
  );

  const handleReset = useCallback(() => {
    inFlightRequest.current = false;
    setMessages([]);
    setIsLoading(false);
    setIsSessionLimitReached(false);
    setSystemLimitMessage(null);
    seenMessageIds.current.clear();
    clearStoredChatHistory();
    getSessionStorage()?.removeItem('sessionToken');
    setNeedsReVerify(true);
    void resetSession(agentEndpoint, selectedEndpoint);
  }, [agentEndpoint, selectedEndpoint]);

  const mapGeneratedQueryToSearchPage = useCallback(
    (queryUrl: string) => {
      const targetPath = (searchPagePath || '/nsdi-search').trim();
      const forwarded = new URLSearchParams();
      forwarded.set('settings', 'any');

      try {
        const parsedSource = new URL(queryUrl);
        parsedSource.searchParams.forEach((value, key) => {
          // Carry through query-building params from the generated Solr URL.
          // The search page can choose which of these to honor.
          forwarded.set(key, value);
        });

        const sourceQuery = (parsedSource.searchParams.get('q') || '').trim();
        const sourcePlace = (
          parsedSource.searchParams.get('place') || ''
        ).trim();
        const decodedQuery = decodeURIComponent(
          sourceQuery.replace(/\+/g, ' '),
        );
        const inferredPlaceFromQuery = (() => {
          const match = decodedQuery.match(
            /\b(?:in|from|for|near|around)\s+([A-Za-z][A-Za-z\s.-]{1,60})(?:\b|$)/i,
          );
          return match ? match[1].trim() : '';
        })();

        if (sourcePlace || inferredPlaceFromQuery) {
          const normalizedPlace = sourcePlace || inferredPlaceFromQuery;
          const normalizedPlaceId =
            parsedSource.searchParams.get('place.id') || '';
          const normalizedFinder =
            parsedSource.searchParams.get('place.finder') || 'esri';
          const normalizedOp =
            parsedSource.searchParams.get('place.op') || 'within';

          forwarded.set('place', normalizedPlace);
          if (normalizedPlaceId) {
            forwarded.set('place.id', normalizedPlaceId);
          }
          forwarded.set('place.finder', normalizedFinder);
          forwarded.set('place.op', normalizedOp);
        }

        // Map Voyager place coordinates to the bbox format expected by the
        // search page state parser so map outlines appear automatically.
        const placeParam = parsedSource.searchParams.get('place') || '';
        if (placeParam && !forwarded.get('bbox')) {
          const coordParts = placeParam
            .replace(/\+/g, ' ')
            .split(/[\s,]+/)
            .filter(Boolean)
            .map(Number);

          if (
            coordParts.length === 4 &&
            coordParts.every((n) => !Number.isNaN(n))
          ) {
            const [minLon, minLat, maxLon, maxLat] = coordParts;
            const bboxParam = [
              `${minLon},${minLat}`,
              `${maxLon},${minLat}`,
              `${maxLon},${maxLat}`,
              `${minLon},${maxLat}`,
            ].join(';;');
            forwarded.set('bbox', bboxParam);
            forwarded.set(
              'place.op',
              parsedSource.searchParams.get('place.op') || 'within',
            );
          }
        }
      } catch {
        // Keep the default settings param when source URL cannot be parsed.
      }

      if (/^https?:\/\//i.test(targetPath)) {
        const parsedTarget = new URL(targetPath);
        forwarded.forEach((value, key) => {
          parsedTarget.searchParams.set(key, value);
        });
        return parsedTarget.toString();
      }

      const normalizedTarget = targetPath.startsWith('/')
        ? targetPath
        : `/${targetPath}`;
      return `${normalizedTarget}?${forwarded.toString()}`;
    },
    [searchPagePath],
  );

  const isSearchPageActive = useCallback((): boolean => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
      const configuredSearchPath = /^https?:\/\//i.test(searchPagePath)
        ? new URL(searchPagePath).pathname
        : searchPagePath;
      const normalizedSearchPath = configuredSearchPath.startsWith('/')
        ? configuredSearchPath
        : `/${configuredSearchPath}`;

      return currentPath === (normalizedSearchPath.replace(/\/+$/, '') || '/');
    } catch {
      return false;
    }
  }, [searchPagePath]);

  const handleQueryClick = useCallback(
    (
      url: string,
      options?: { historyMode?: 'push' | 'replace'; closeModal?: boolean },
    ) => {
      if (!isSearchPageActive()) {
        return;
      }

      if (options?.closeModal) {
        setIsModalOpen(false);
      }

      const transformedUrl = /^https?:\/\//i.test(url)
        ? mapGeneratedQueryToSearchPage(url)
        : url;

      if (transformedUrl.startsWith('/')) {
        // In-place URL updates keep the chat open while search results react
        // via popstate listeners in the search context.
        const separator = transformedUrl.includes('?') ? '&' : '?';
        const nextUrl = `${transformedUrl}${separator}_t=${Date.now()}`;
        if (typeof window !== 'undefined') {
          const mode =
            options?.historyMode === 'replace' ? 'replaceState' : 'pushState';
          window.history[mode](null, '', nextUrl);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      } else {
        // For absolute URLs (including external searchPagePath), open in new tab
        window.open(transformedUrl, '_blank');
      }
    },
    [isSearchPageActive, mapGeneratedQueryToSearchPage],
  );

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    if (!autoApplyGeneratedSearch) {
      return;
    }

    if (!isSearchPageActive()) {
      return;
    }

    if (isLoading || inFlightRequest.current) {
      return;
    }

    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg.role !== 'assistant' || msg.isStreaming) continue;
      const generatedQueryUrl = getGeneratedQueryUrl(msg);
      if (
        !generatedQueryUrl ||
        generatedQueryUrl === lastAppliedQueryUrl.current
      )
        continue;

      lastAppliedQueryUrl.current = generatedQueryUrl;
      handleQueryClick(generatedQueryUrl, {
        historyMode: 'replace',
        closeModal: false,
      });
      break;
    }
  }, [
    messages,
    handleQueryClick,
    isLoading,
    autoApplyGeneratedSearch,
    isEditMode,
    isSearchPageActive,
  ]);

  const handleFacetClick = useCallback(
    (label: string) => {
      void handleSend(label);
    },
    [handleSend],
  );

  const handleSuggestionClick = useCallback(
    (text: string) => {
      void handleSend(text);
    },
    [handleSend],
  );

  return (
    <div className="voyager-enhanced-search-block">
      {/* FAB — morphs between button and close states */}
      <button
        onClick={() => {
          if (systemLimitMessage) return;
          setIsModalOpen((prev) => !prev);
        }}
        className={`ves-fab ${fabPosition === 'bottom-left' ? 'ves-fab--left' : ''} ${isModalOpen ? 'ves-fab--active' : ''}`}
        aria-label={
          isModalOpen ? 'Close Enhanced Search' : 'Open Enhanced Search'
        }
      >
        {/* Icon crossfade */}
        <FontAwesomeIcon
          icon={faCommentDots}
          className={`ves-fab-icon ${isModalOpen ? 'ves-fab-icon--hidden' : ''}`}
        />
        <FontAwesomeIcon
          icon={faXmark}
          className={`ves-fab-icon ves-fab-icon--close ${isModalOpen ? '' : 'ves-fab-icon--hidden'}`}
        />

        {/* Pulse ring — only when closed */}
        {!isModalOpen && <span className="ves-fab-pulse" />}
      </button>

      {systemLimitMessage && !isModalOpen && (
        <div className="ves-system-limit-alert" role="alert">
          {systemLimitMessage}
        </div>
      )}

      {/* Chat Modal */}
      <ChatModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sessionId={getSessionId()}
        messages={messages}
        isLoading={isLoading}
        isSessionLimitReached={isSessionLimitReached}
        forceVerified={isEditMode && Boolean(data?.testRateLimitNonce)}
        onSend={handleSend}
        onReset={handleReset}
        seenMessageIds={seenMessageIds.current}
        onQueryClick={handleQueryClick}
        onFacetClick={handleFacetClick}
        onSuggestionClick={handleSuggestionClick}
        verificationBaseUrl={verificationBaseUrl}
        turnstileSiteKey={turnstileSiteKey}
        allowLocalDevBypass={allowLocalDevBypass}
        showDebugInfo={showDebugInfo}
        showSessionId={showSessionId}
        placeholder={placeholder}
        headerTitle={headerTitle}
        headerSubtitle={headerSubtitle}
        greetingMessage={greetingMessage}
        projectInfoUrl={projectInfoUrl}
        projectInfoLinkText={projectInfoLinkText}
        forceReVerify={needsReVerify}
        onReVerifyHandled={() => setNeedsReVerify(false)}
        onVerified={handleChatVerified}
        feedbackEnabled={feedbackEnabled}
        feedbackStyle={feedbackStyle}
        feedbackTagsPositive={feedbackTagsPositive}
        feedbackTagsNegative={feedbackTagsNegative}
        onSubmitFeedback={handleSubmitFeedback}
      />
    </div>
  );
};

export default VoyagerEnhancedSearchBlock;
